/**
 * Wires Usercentrics consent → tracker init/teardown.
 * Rendered in Providers.
 */
import { ReactElement, useEffect } from 'react';
import { USERCENTRICS_ENABLE } from 'config/env';
import { initFirebaseAnalytics, teardownFirebaseAnalytics } from 'config/firebaseConfig';
import { capturePromiseRejection } from 'utils/capturePromiseRejection';
import { initHotjar, teardownHotjar } from './initHotjar';
import { initMetaPixel, isMetaPixelEnabled, teardownMetaPixel } from './initMetaPixel';
import { isMetaPixelConsentServiceConfigured, UC_SERVICES } from './usercentricsConfig';
import { useServiceConsent } from './useServiceConsent';

const initAllTrackers = (): void => {
  capturePromiseRejection(initFirebaseAnalytics(), 'Failed to initialize Firebase analytics');
  initHotjar();
  if (isMetaPixelEnabled()) {
    initMetaPixel();
  }
};

export const TrackingInitializer = (): ReactElement | null => {
  const firebaseConsent = useServiceConsent(UC_SERVICES.firebase);
  const hotjarConsent = useServiceConsent(UC_SERVICES.hotjar);
  const metaPixelConsent = useServiceConsent(UC_SERVICES.metaPixel);

  useEffect(() => {
    if (!USERCENTRICS_ENABLE) {
      initAllTrackers();
    }
  }, []);

  useEffect(() => {
    if (!USERCENTRICS_ENABLE) return;

    if (firebaseConsent === true) {
      capturePromiseRejection(initFirebaseAnalytics(), 'Failed to initialize Firebase analytics');
    } else if (firebaseConsent === false) {
      capturePromiseRejection(teardownFirebaseAnalytics(), 'Failed to teardown Firebase analytics');
    }
  }, [firebaseConsent]);

  useEffect(() => {
    if (!USERCENTRICS_ENABLE) return;

    if (hotjarConsent === true) {
      initHotjar();
    } else if (hotjarConsent === false) {
      teardownHotjar();
    }
  }, [hotjarConsent]);

  useEffect(() => {
    if (!USERCENTRICS_ENABLE || !isMetaPixelEnabled()) return;

    if (!isMetaPixelConsentServiceConfigured()) {
      console.warn(
        '[Meta Pixel] UC_SERVICES.metaPixel is not set in usercentricsConfig.ts; pixel will not load while Usercentrics is enabled.'
      );
      return;
    }

    if (metaPixelConsent === true) {
      initMetaPixel();
    } else if (metaPixelConsent === false) {
      teardownMetaPixel();
    }
  }, [metaPixelConsent]);

  return null;
};
